import { Component, HostListener, inject } from '@angular/core';
import { RenterInfoComponent } from 'src/app/pages/form/renter-info/renter-info.component';
import { BikesSelectionComponent } from 'src/app/pages/form/bikes-selection/bikes-selection.component';
import { TermsAndConditionsComponent } from 'src/app/pages/form/terms-and-conditions/terms-and-conditions.component';
import { PaymentSelectionComponent } from 'src/app/pages/form/payment-selection/payment-selection.component';
import { BookingHandlerComponent } from 'src/app/pages/form/booking-handler/booking-handler.component';
import {
  RentalPointsSelectionComponent
} from 'src/app/pages/form/rental-points-selection/rental-points-selection.component';
import {
  AccessoriesSelectionComponent
} from 'src/app/pages/form/accessories-selection/accessories-selection.component';
import { PriceStateComponent } from 'src/app/pages/form/price-state/price-state.component';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { StepsService } from 'src/app/services/steps.service';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { NgStyle } from '@angular/common';
import { Icons } from 'src/app/icons';
import { logger } from 'src/app/utils';
import { InvoiceDataComponent } from 'src/app/pages/form/invoice-data/invoice-data.component';
import { SpacerComponent } from "src/app/ui/atoms/spacer/spacer.component";
import { HeaderComponent } from 'src/app/layout/header/header.component';
import { FooterComponent } from 'src/app/layout/footer/footer.component';
import { environment } from 'src/environments/environment';
import { TestBannerComponent } from 'src/app/shared/components/test-banner/test-banner.component';
import { TestDataComponent } from 'src/app/shared/components/test-data-filler/test-data.component';
import { KENDO_DIALOGS, DialogComponent } from '@progress/kendo-angular-dialog';
import { LoaderComponent } from 'src/app/ui/atoms/loader/loader.component';
import { FormService } from 'src/app/services/form.service';
import { TestDataService } from 'src/app/services/testData.service';
import { HOBEX, PAYMENT_WITH_WEBHOOKS } from 'src/app/app.constants';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
    selector: 'app-form',
    imports: [
        RentalPointsSelectionComponent,
        RentalPointsSelectionComponent,
        AccessoriesSelectionComponent,
        TermsAndConditionsComponent,
        PaymentSelectionComponent,
        BookingHandlerComponent,
        BikesSelectionComponent,
        ReactiveFormsModule,
        RenterInfoComponent,
        PriceStateComponent,
        ButtonComponent,
        TranslatePipe,
        NgStyle,
        InvoiceDataComponent,
        SpacerComponent,
        HeaderComponent,
        FooterComponent,
        TestBannerComponent,
        TestDataComponent,
        DialogComponent,
        KENDO_DIALOGS,
        LoaderComponent,
    ],
    providers: [
        FormService,
        StepsService,
        TestDataService
    ],
    templateUrl: './form.component.html',
    styleUrl: './form.component.scss'
})
export class FormComponent {
  private readonly succeedCode: string;
  readonly icons = Icons;

  loaderService = inject(LoaderService);
  formService = inject(FormService);
  s = inject(StepsService);

  form = this.formService.form;

  showTestBanner = false;
  showTestDataFiller = false;

  constructor() {
    this.succeedCode = environment.production
      ? HOBEX.TRANSACTION_SUCCEEDED
      : HOBEX.TEST_TRANSACTION_SUCCEEDED;

    if (!environment.production) {
      this.showTestDataFiller = this.formService.params['helper']=== 'true';
      this.showTestBanner = !this.showTestDataFiller;
    }

    this.setViewportPadding();
  }

  private setViewportPadding() {
    const padding = this.formService.params['padding']?.trim();
    const rootElement = document.documentElement;
    const validPaddingRegex = /^\d+(\.\d+)?(px|rem|%)?$/;
    if (padding && validPaddingRegex.test(padding)) {
      rootElement.style.setProperty('--dynamic-padding', padding);
    } else {
      rootElement.style.removeProperty('--dynamic-padding');
    }
  }

  private async isValidForm(formId: string): Promise<boolean> {
    const form = document.getElementById(formId) as HTMLFormElement;
    if (!form) {
      logger.e(`Form ${formId} not found`);
      return false;
    }
    form.requestSubmit();

    if (formId === this.s.stepsData.accessoriesSelection.id) {
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    if (formId === this.s.stepsData.rentalPointsSelection.id) {
      if (!this.form().startRental || !this.form().endRental || !this.form().from || !this.form().to) {
        return false;
      }
    }

    if (formId === this.s.stepsData.termsAndConditions.id) {
      if (!this.form().privacy) return false;
      if (typeof this.form().marketing === 'undefined') return false;
    }

    const invalidElements = form.querySelectorAll('.k-form-field-error') || [];
    return invalidElements.length === 0 && form.checkValidity();
  }

  showNoProductsDialog = false;
  hideBackButton = false;

  closeNoProductsDialog() {
    this.showNoProductsDialog = false;
  }

  async next() {
    if (this.s.currentStep() === this.s.stepsData.accessoriesSelection.step) {
      if ((!this.form().bikes && !this.form().accessories) ||
        (this.form().bikes?.length === 0 && this.form().accessories?.length === 0)) {
        this.showNoProductsDialog = true;
        return;
      }
    }
    const formId = this.s.currentFormId();
    const isValid = await this.isValidForm(formId);
    if (!isValid) return;
    logger.i(`go to next step form ${formId} with data`, this.form());
    this.s.nextStepHandler();
  }

  back() {
    this.s.previousStepHandler();
  }

  @HostListener('window:message', ['$event'])
  onMessage(e: any) {
    const expectedOrigin = environment.domainUrl.replace(/\/$/, '');
    if (e.origin !== expectedOrigin) return;
    if (e.data && e.data.type) {
      if (e.data.type === 'hobexError') {
        logger.w('form hobex error', e.data.type?.error);
        this.hideBackButton = true;
      } else if (e.data.type === 'hobexResponse' && e.data.data) {
        const successCondition = PAYMENT_WITH_WEBHOOKS
          ? (e.data.data?.resultCode === this.succeedCode)
          : (e.data.data?.status === 200);
        if (successCondition) {
          logger.i('hide back button');
          this.hideBackButton = true;
        }
      }
    }
  }


}
